import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Link } from 'react-router-dom';
import { PATHS } from '../../../constants/routes';
import { SOCIALS } from '../../../constants/common';
import { useIsScrolled, useIsBottomReached } from '../../../hooks/useScrollInfo';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import FloatingContactsMenu from './floatingContactsMenu/FloatingContactsMenu';
import Feedback from './feedback/Feedback';
import SidePopup from '../SidePopup/SidePopup';
import AuthPopupContent from '../Header/authPopup/AuthPopupContent';
import './footer.scss';

type FooterProps = {
    isMobile?: boolean;
};

const Footer: React.FC<FooterProps> = ({ isMobile }) => {
    const scrolled = useIsScrolled(0);
    const bottomReached = useIsBottomReached(4);
    const showFloating = scrolled && !bottomReached;
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    const [authPopupOpen, setAuthPopupOpen] = useState(false);
    const closeAuthPopup = useCallback(() => setAuthPopupOpen(false), []);
    const openAuthPopup = useCallback(() => setAuthPopupOpen(true), []);

    return (
        <footer className="footer">
            <div className="footer__logoBox">
                <div className="footer__logoBox_wrapper">
                    <Link className="footer__logoBox_link" to="/" aria-label="На главную">
                        <img className="footer__logoBox_link_img" src="/logo.png" alt="С. Габай — логотип" />
                    </Link>
                    <p className="footer__logoBox_desc">
                        Только натуральный шелк <br className="footer__logoBox_desc_br-mob" />
                        Малбери, <br className="footer__logoBox_desc_br-desc" />
                        Тусса и Голд - с доставкой <br className="footer__logoBox_desc_br-mob" />
                        по всей России.
                    </p>
                </div>
                <div className="footer__infoBox">
                    <div className="footer__infoBox_wrapper">
                        <div className="footer__infoBox_linkBox">
                            <Link
                                className="footer__infoBox_elem footer__infoBox_elem_hover"
                                to="/customers?section=policy"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Политика конфиденциальности
                            </Link>
                            <Link
                                className="footer__infoBox_elem footer__infoBox_elem_hover"
                                to="/customers?section=offer"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Условия продажи
                            </Link>
                        </div>
                        <span className="footer__infoBox_elem">© S-Gabai {new Date().getFullYear()}</span>
                    </div>
                    <div className="footer__infoBox_wrapper">
                        <span className="footer__infoBox_elem">
                            ООО "Интрейд", ИНН 5044123930, <br />
                            КПП 504701001, ОГРН 1215000130619
                        </span>
                        <span className="footer__infoBox_elem">
                            Юридический адрес: 141580, МО, <br />
                            с. Чашниково, ул. Промышленная, стр. 1 к. 4, офис 114
                        </span>
                    </div>
                </div>
            </div>
            <div className="footer__wrapper">
                <nav className="footer__navBox" aria-label="Навигация по сайту">
                    <details className="footer__navBox_group" data-id="catalog" open={isMobile}>
                        <summary className="footer__navBox_summary">Каталог</summary>
                        <ul className="footer__navBox_list">
                            <li>
                                <Link className="footer__navBox_link" to="/catalog/pillows">
                                    Шёлковые подушки
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to="/catalog/blankets">
                                    Шёлковые одеяла
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to="/catalog/bed-linen">
                                    Постельное белье
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to="/catalog/accessories">
                                    Аксессуары
                                </Link>
                            </li>
                            <li className="footer__navBox_all">
                                <Link className="footer__navBox_linkAll" to={PATHS.showcase} aria-label="Смотреть весь каталог">
                                    Смотреть весь каталог →
                                </Link>
                            </li>
                        </ul>
                    </details>
                    <details className="footer__navBox_group" data-id="info" open={!isMobile}>
                        <summary className="footer__navBox_summary">Информация</summary>
                        <ul className="footer__navBox_list">
                            <li>
                                <Link className="footer__navBox_link" to={PATHS.aboutCompany}>
                                    О компании
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to={PATHS.deliveryPayment}>
                                    Доставка и оплата
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to={PATHS.loyalty}>
                                    Программа лояльности
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to={`${PATHS.showcase}/discounts`}>
                                    Акции
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to={PATHS.hotelProducts}>
                                    Партнёрам
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to={PATHS.customers}>
                                    Покупателям
                                </Link>
                            </li>
                            <li>
                                <Link className="footer__navBox_link" to={PATHS.contacts}>
                                    Контакты
                                </Link>
                            </li>
                        </ul>
                    </details>
                    {isAuthenticated ? (
                        <Link className="footer__navBox_elem" to={PATHS.account}>
                            Личный кабинет
                        </Link>
                    ) : (
                        <button className="footer__navBox_elem footer__navBox_elem_btn" onClick={openAuthPopup}>
                            Личный кабинет
                        </button>
                    )}
                    <Link className="footer__navBox_elem" to={PATHS.siteMap}>
                        Карта сайта
                    </Link>
                </nav>
                <div className="footer__contacts">
                    <p className="footer__contacts_title">Контакты</p>
                    <div className="footer__contacts_container">
                        <a href="tel:+74952806055" className="footer__contacts_container_value">
                            +7-495-280-60-55
                        </a>
                        <span className="footer__contacts_container_label">Служба поддержки клиентов</span>
                    </div>
                    <div className="footer__contacts_container">
                        <a href="mailto:sales@s-gabai.ru" className="footer__contacts_container_value">
                            sales@s-gabai.ru
                        </a>
                        <span className="footer__contacts_container_label">По вопросам сотрудничества</span>
                    </div>
                    <div className="footer__contacts_container">
                        <a className="footer__contacts_container_value" href="https://yandex.ru/maps/-/CHdaa8Ms" target="_blank" rel="noopener noreferrer">
                            141580, МО, г. о. Химки, с. Чашниково, <br />
                            ул. Промышленная, стр. 1, корп. 4, оф. 114
                        </a>
                        <span className="footer__contacts_container_label">Адрес офиса для самовывоза</span>
                    </div>
                    <div className="footer__contacts_container">
                        <span className="footer__contacts_container_value">Пн–Пт: 10:00–18:00</span>
                        <span className="footer__contacts_container_label">График работы офиса</span>
                    </div>
                    <div className="footer__contacts_buttons">
                        {SOCIALS.vk && (
                            <a className="footer__contacts_buttons_elem" href={SOCIALS.vk} target="_blank" rel="noopener noreferrer" aria-label="Мы во ВКонтакте">
                                ВКонтакте
                            </a>
                        )}
                        {SOCIALS.ig && (
                            <a className="footer__contacts_buttons_elem" href={SOCIALS.ig} target="_blank" rel="noopener noreferrer" aria-label="Мы в Instagram">
                                Instagram
                            </a>
                        )}
                        {/* {SOCIALS.tg && (
                            <a className="footer__contacts_buttons_elem" href={SOCIALS.tg} target="_blank" rel="noopener noreferrer" aria-label="Мы в Telegram">
                                Telegram
                            </a>
                        )} */}
                        {/* <Feedback /> */}
                        {/* {SOCIALS.wa && (
                            <a className="footer__contacts_buttons_elem" href={SOCIALS.wa} target="_blank" rel="noopener noreferrer" aria-label="Мы в WhatsApp">
                                WhatsApp
                            </a>
                        )} */}
                        <div className="footer__contacts_buttons_box">
                            <Feedback />
                            <ActionLinkButton
                                href={SOCIALS.tg}
                                target="_blank"
                                ariaLabel="Мы в Telegram"
                                iconAlt="TG"
                                iconSrc="/icons/mobile/TG.svg"
                                iconSrcDark="/icons/mobile/TG_w.svg"
                            />
                            <ActionLinkButton
                                href={SOCIALS.wa}
                                target="_blank"
                                ariaLabel="Мы в WhatsApp"
                                iconAlt="WA"
                                iconSrc="/icons/mobile/WA.svg"
                                iconSrcDark="/icons/mobile/WA_w.svg"
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* {showToTop ? (
                isMobile ? (
                    <div className="footer__floatingBox">
                        <ActionLinkButton
                            href="tel:+74952806055"
                            ariaLabel="Позвонить нам"
                            iconAlt="Позвонить"
                            iconSrc="/icons/mobile/phone.svg"
                            iconSrcDark="/icons/mobile/phone_w.svg"
                            className="footer__floatingBox_phone"
                        />
                    </div>
                ) : (
                    <ActionLinkButton
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        iconSrc="/icons/mobile/arrow-up.svg"
                        iconSrcDark="/icons/mobile/arrow-up_w.svg"
                        ariaLabel="Наверх"
                        iconAlt="Наверх"
                        className="footer__toTop"
                    />
                )
            ) : null} */}
            {showFloating ? <FloatingContactsMenu telegramHref={SOCIALS.tg} maxHref={SOCIALS.mx} emailHref="mailto:sales@s-gabai.ru" /> : null}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'Organization',
                        '@id': 'https://s-gabai.ru/#organization',
                        name: 'S-Gabai',
                        legalName: 'ООО «Интрейд»',
                        url: 'https://s-gabai.ru',
                        logo: 'https://s-gabai.ru/logo.png',
                        email: 'sales@s-gabai.ru',
                        telephone: '+7-495-280-60-55',
                        address: {
                            '@type': 'PostalAddress',
                            postalCode: '141580',
                            addressCountry: 'RU',
                            addressRegion: 'Московская область',
                            addressLocality: 'г.о. Химки, с. Чашниково',
                            streetAddress: 'ул. Промышленная, стр. 1, корп. 4, оф. 114',
                        },
                        contactPoint: [
                            {
                                '@type': 'ContactPoint',
                                contactType: 'customer support',
                                telephone: '+7-495-280-60-55',
                                areaServed: 'RU',
                                availableLanguage: ['ru'],
                                hoursAvailable: {
                                    '@type': 'OpeningHoursSpecification',
                                    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                                    opens: '10:00',
                                    closes: '18:00',
                                },
                            },
                        ],
                        // "sameAs": ["https://vk.com/...", "https://instagram.com/...", "https://t.me/..."]
                    }),
                }}
            />
            {/* Попап авторизации/регистрации (справа) */}
            <SidePopup open={authPopupOpen} onClose={closeAuthPopup} className="headerAuthPopup" showCloseButton={false}>
                <AuthPopupContent onClose={closeAuthPopup} />
            </SidePopup>
        </footer>
    );
};

export default Footer;
